import { Component, Inject, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SharedModule } from "@frontend/app/shared/shared.module";
import { MasterService } from "@frontend/app/serivces/master.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { SVG_ICONS } from "@frontend/app/models/icons";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

@Component({
  selector: "app-event-details",
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: "./event-details.component.html",
  styleUrl: "./event-details.component.scss",
})
export class EventDetailsComponent implements OnInit {
  private masterService = inject(MasterService);

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private dialogRef: MatDialogRef<EventDetailsComponent>) {}

  ngOnInit() {
    if (this.data.eventId) {
      this.getEventDetails(this.data.eventId);
    }
  }

  //#region variables
  eventData: any;
  participants: any[] = [];
  displayedColumns: string[] = ["StudentName", "MobileNo", "Gender", "DOB", "City", "State"];
  dataSource = new MatTableDataSource<any>();
  readonly SVG_ICONS = SVG_ICONS;
  //#endregion

  getEventDetails(id: number) {
    this.masterService.getEventByID(id).subscribe((res: any) => {
      this.eventData = res.data;
    });

    this.masterService.getEventRegistrationListByID(id).subscribe((res: any) => {
      if (res.data && Array.isArray(res.data)) {
        this.participants = res.data.map((item: any) => ({
          ...item,
          StudentName: item.Student ? `${item.Student.FirstName} ${item.Student.MiddleName || ""} ${item.Student.LastName}` : "",
          MobileNo: item.Student ? item.Student.MobileNo : "",
          Gender: item.Student ? item.Student.Gender : "",
          DOB: item.Student ? item.Student.DOB : "",
          City: item.Student ? item.Student.City : "",
          State: item.Student ? item.Student.State : "",
          IsActive: item.IsActive,
        }));
        this.dataSource.data = this.participants;
      } else {
        this.eventData = res.data;
        if (res.data.Participants) {
          this.participants = res.data.Participants;
          this.dataSource.data = this.participants;
        } else if (res.data.EventRegistrations) {
          this.participants = res.data.EventRegistrations;
          this.dataSource.data = this.participants;
        }
      }
    });
  }

  onClose() {
    this.dialogRef.close();
  }

  async downloadReport() {
    if (!this.eventData) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Event Report");

    // Define styles
    const titleFont = { name: "Arial", size: 16, bold: true, color: { argb: "FFFFFFFF" } };
    const titleFill: ExcelJS.Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } }; // Dark Blue

    const headerFont = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
    const headerFill: ExcelJS.Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2F75B5" } }; // Medium Blue

    const dataFont = { name: "Arial", size: 10 };
    const borderStyle: Partial<ExcelJS.Borders> = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    // 1. Add Title
    worksheet.mergeCells("A1:F1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = this.eventData.EventName;
    titleCell.font = titleFont;
    titleCell.fill = titleFill;
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    worksheet.getRow(1).height = 35;

    // 2. Add Event Details (Key-Value pairs)
    const formatDate = (date: any) => {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-GB");
    };

    const details = [
      { label: "Description", value: this.eventData.EventDescription },
      { label: "Type", value: this.eventData.EventType },
      { label: "Venue", value: `${this.eventData.Venue}, ${this.eventData.City}` },
      { label: "Fee", value: this.eventData.RegistrationFee },
      { label: "Start Date", value: formatDate(this.eventData.StartDate) },
      { label: "End Date", value: formatDate(this.eventData.EndDate) },
      { label: "Reg. Last Date", value: formatDate(this.eventData.RegistrationLastDate) },
    ];

    let currentRow = 3;
    details.forEach(detail => {
      const labelCell = worksheet.getCell(`A${currentRow}`);
      labelCell.value = detail.label;
      labelCell.font = { name: "Arial", size: 11, bold: true };
      labelCell.alignment = { vertical: "middle" };

      // Merge B to F for value to give it space
      worksheet.mergeCells(`B${currentRow}:F${currentRow}`);
      const valueCell = worksheet.getCell(`B${currentRow}`);
      valueCell.value = detail.value;
      valueCell.font = dataFont;
      valueCell.alignment = { vertical: "middle", wrapText: true };

      currentRow++;
    });

    currentRow += 1; // Spacer

    // 3. Participants Header
    const headers = ["Student Name", "Mobile No", "Gender", "DOB", "City", "State"];
    const headerRow = worksheet.getRow(currentRow);
    headers.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.font = headerFont;
      cell.fill = headerFill;
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = borderStyle;
    });
    headerRow.height = 25;
    currentRow++;

    // 4. Participants Data
    this.participants.forEach(p => {
      const row = worksheet.getRow(currentRow);
      const values = [p.StudentName, p.MobileNo, p.Gender, formatDate(p.DOB), p.City, p.State];

      values.forEach((val, index) => {
        const cell = row.getCell(index + 1);
        cell.value = val;
        cell.font = dataFont;
        cell.border = borderStyle;
        cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      });
      currentRow++;
    });

    // 5. Set Column Widths
    worksheet.columns = [
      { width: 25 }, // Student Name / Label
      { width: 15 }, // Mobile No
      { width: 10 }, // Gender
      { width: 15 }, // DOB
      { width: 20 }, // City
      { width: 20 }, // State
    ];

    // Generate and Save
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `${this.eventData.EventName.replace(/[^a-zA-Z0-9]/g, "_")}_Report.xlsx`;
    saveAs(new Blob([buffer]), fileName);
  }
}
